import { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Dropdown,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function Product() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [category_id, setCategoryId] = useState("");
  const [product_name, setProductName] = useState("");
  const [product_description, setProductDescription] = useState("");
  const [status, setStatus] = useState("active");
  const [unit_price, setUnitPrice] = useState("");
  const [supplier_id, setSupplierId] = useState("");
  const [unit, setUnit] = useState("");

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showAll, setShowAll] = useState(true);

  const unitOptions = [
    { value: "number", label: "Number" },
    { value: "liter", label: "Liter" },
    { value: "kg", label: "Kilogram" },
    { value: "gram", label: "Gram" },
    { value: "meter", label: "Meter" },
    { value: "piece", label: "Piece" },
    { value: "box", label: "Box" },
    { value: "dozen", label: "Dozen" },
  ];

  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    const tableColumn = [
      "No", 
      "Product Name", 
      "Category", 
      "Unit", 
      "Unit Price", 
      "Supplier", 
      "Status"
    ];

    const tableRows = products.map((item, index) => [
      index + 1,
      item.product_name,
      item.category_name || 'N/A',
      item.unit,
      `₹${parseFloat(item.unit_price || 0).toFixed(2)}`,
      item.vendor_name || 'N/A',
      item.status,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
    });

    doc.save("products.pdf");
  };

  useEffect(() => {
    // Fetch products
    api
      .get("/api/products", {
        withCredentials: true,
      })
      .then((res) => {
        const productData = res.data.products || [];
        const formattedData = productData
          .map((item, ind) => ({
            id: item.id,
            index: ind + 1,
            product_name: item.product_name,
            category_id: item.category_id,
            category_name: item.category_name,
            product_description: item.product_description,
            status: item.status,
            unit_price: item.unit_price,
            supplier_id: item.supplier_id,
            supplier_name: item.supplier_name || 'N/A',
            unit: item.unit,
            created_at: item.created_at,
            updated_at: item.updated_at,
          }))
          .reverse();
        setProducts(formattedData);
      })
      .catch((err) => {
        setProducts([]);
      });

    // Fetch categories for dropdown
    api
      .get("/api/product-categories", {
        withCredentials: true,
      })
      .then((res) => {
        const categoryData = res.data.productCategories || [];
        setCategories(categoryData);
      })
      .catch((err) => {
        setCategories([]);
      });

    // Fetch suppliers for dropdown
    api
      .get("/api/suppliers", {
        withCredentials: true,
      })
      .then((res) => {
        const supplierData = res.data.suppliers || [];
        setSuppliers(supplierData);
      })
      .catch((err) => {
        setSuppliers([]);
      });
  }, []);

  const handleAddProduct = () => {
    // Form validation
    if (!category_id) {
      setSuccessMsg("Please select a category!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!product_name || product_name.trim() === "") {
      setSuccessMsg("Please enter product name!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!product_description || product_description.trim() === "") {
      setSuccessMsg("Please enter product description!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!unit_price || unit_price <= 0) {
      setSuccessMsg("Please enter a valid unit price!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!supplier_id) {
      setSuccessMsg("Please select a supplier!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!unit) {
      setSuccessMsg("Please select a unit!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    api
      .post(
        "/api/products",
        {
          category_id: category_id,
          product_name: product_name,
          product_description: product_description,
          status: status,
          unit_price: unit_price,
          supplier_id: supplier_id,
          unit: unit,
        },
        { withCredentials: true },
      )
      .then((res) => {
        const newProduct = {
          id: res.data.id,
          index: products.length + 1,
          product_name: product_name,
          category_id: category_id,
          category_name: categories.find(c => c.id === parseInt(category_id))?.category_name || '',
          product_description: product_description,
          status: status,
          unit_price: unit_price,
          supplier_id: supplier_id,
          supplier_name: suppliers.find(s => s.id === parseInt(supplier_id))?.supplier_name || '',
          unit: unit,
        };

        setProducts([newProduct, ...products]);

        // Reset form
        setCategoryId("");
        setProductName("");
        setProductDescription("");
        setStatus("active");
        setUnitPrice("");
        setSupplierId("");
        setUnit("");
        setShowModal(false);

        setSuccessMsg("Product added successfully!");

        setTimeout(() => {
          setSuccessMsg("");
        }, 3000);
      })
      .catch((err) => {
        setSuccessMsg("Error adding product!");
        setTimeout(() => {
          setSuccessMsg("");
        }, 3000);
      });
  };

  const handleDeleteProduct = (id) => {
    if (!window.confirm("Are you sure you want to delete this product?"))
      return;

    api
      .delete(`/api/products/${id}`, {
        withCredentials: true,
      })
      .then(() => {
        const updatedProducts = products.filter((item) => item.id !== id);
        setProducts(updatedProducts);

        setSuccessMsg("Product deleted successfully!");

        setTimeout(() => {
          setSuccessMsg("");
        }, 3000);
      })
      .catch((err) => {
        setSuccessMsg("Error deleting product!");
        setTimeout(() => {
          setSuccessMsg("");
        }, 3000);
      });
  };

  const handleUpdateProduct = () => {
    // Form validation
    if (!category_id) {
      setSuccessMsg("Please select a category!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!product_name || product_name.trim() === "") {
      setSuccessMsg("Please enter product name!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!product_description || product_description.trim() === "") {
      setSuccessMsg("Please enter product description!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!unit_price || unit_price <= 0) {
      setSuccessMsg("Please enter a valid unit price!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!supplier_id) {
      setSuccessMsg("Please select a supplier!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    if (!unit) {
      setSuccessMsg("Please select a unit!");
      setTimeout(() => setSuccessMsg(""), 3000);
      return;
    }

    api
      .put(
        `/api/products/${editData.id}`,
        {
          category_id: category_id,
          product_name: product_name,
          product_description: product_description,
          status: status,
          unit_price: unit_price,
          supplier_id: supplier_id,
          unit: unit,
        },
        { withCredentials: true },
      )
      .then(() => {
        const updatedProducts = products.map((item) => {
          if (item.id === editData.id) {
            return {
              ...item,
              category_id: category_id,
              category_name: categories.find(c => c.id === parseInt(category_id))?.category_name || item.category_name,
              product_name: product_name,
              product_description: product_description,
              status: status,
              unit_price: unit_price,
              supplier_id: supplier_id,
              supplier_name: suppliers.find(s => s.id === parseInt(supplier_id))?.supplier_name || item.supplier_name,
              unit: unit,
            };
          }
          return item;
        });

        setProducts(updatedProducts);

        // Reset form
        setCategoryId("");
        setProductName("");
        setProductDescription("");
        setStatus("active");
        setUnitPrice("");
        setSupplierId("");
        setUnit("");
        setEditData(null);
        setShowModal(false);

        setSuccessMsg("Product updated successfully!");

        setTimeout(() => {
          setSuccessMsg("");
        }, 3000);
      })
      .catch((err) => {
        setSuccessMsg("Error updating product!");
        setTimeout(() => {
          setSuccessMsg("");
        }, 3000);
      });
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      alert("Please select at least one product to delete.");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to delete selected products permanently?",
      )
    )
      return;

    try {
      await Promise.all(
        selectedRows.map((id) =>
          api.delete(`/api/products/${id}`, {
            withCredentials: true,
          }),
        ),
      );

      const updatedProducts = products.filter(
        (item) => !selectedRows.includes(item.id),
      );
      setProducts(updatedProducts);

      setSelectedRows([]);
      setShowCheckbox(false);

      setSuccessMsg("Selected products deleted permanently!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (error) { 
    }
  };

  const handleStatusToggle = (item) => {
    const newStatus = item.status === "active" ? "inactive" : "active";
    
    api
      .put(
        `/api/products/${item.id}`,
        {
          category_id: item.category_id,
          product_name: item.product_name,
          product_description: item.product_description,
          status: newStatus,
          unit_price: item.unit_price,
          supplier_id: item.supplier_id,
          unit: item.unit,
        },
        { withCredentials: true },
      )
      .then(() => {
        const updatedProducts = products.map((product) => {
          if (product.id === item.id) {
            return {
              ...product,
              status: newStatus,
            };
          }
          return product;
        });

        setProducts(updatedProducts);
        setSuccessMsg(`Product status changed to ${newStatus}!`);

        setTimeout(() => {
          setSuccessMsg("");
        }, 3000);
      })
      .catch((err) => {
        // Handle status update error
      });
  };

  // SEARCH FILTER
  const filteredProducts = products.filter((item) =>
    item.product_name.toLowerCase().includes(search.toLowerCase()) ||
    (item.category_name && item.category_name.toLowerCase().includes(search.toLowerCase())) ||
    (item.supplier_name && item.supplier_name.toLowerCase().includes(search.toLowerCase()))
  );

  // PAGINATION
  const totalRows = filteredProducts.length;
  const totalPages =
    rowsPerPage >= totalRows ? 1 : Math.ceil(totalRows / rowsPerPage);

  const startIndex = (currentPage - 1) * rowsPerPage;

  const paginatedProducts = showAll
    ? filteredProducts
    : filteredProducts.slice(startIndex, startIndex + rowsPerPage);

  return (
    <section>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Product", path: "/product/product master" },
          { name: "Product Master" },
        ]}
      />

      <div className="mb-3">
        <Row>
          <Col lg={12}>
            <Card body>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-2">
                  <Button
                    className="btn-icon d-flex align-items-center text-white border-0"
                    style={{ backgroundColor: "#663399" }}
                    onClick={() => {
                      setEditData(null);
                      setShowModal(true);
                    }}
                  >
                    <span className="ul-btn__icon">
                      <i className="i-Conference text-18"></i>
                    </span>
                    <span className="ul-btn__text ml-2">Add Product</span>
                  </Button>

                  <Button
                    className="btn-icon d-flex align-items-center text-white border-0 shadow-none"
                    style={{ backgroundColor: "#663399" }}
                    onClick={() => navigate("/product/import")}
                  >
                    <span className="ul-btn__icon">
                      <i className="i-Upload text-18"></i>
                    </span>
                    <span className="ul-btn__text ml-2">Import Product</span>
                  </Button>

                  <Button
                    className="btn-icon d-flex align-items-center text-white border-0 shadow-none"
                    style={{ backgroundColor: "#c4302b" }}
                    onClick={() => {
                      if (showCheckbox && selectedRows.length > 0) {
                        handleBulkDelete();
                      } else {
                        setShowCheckbox(true);
                      }
                    }}
                  >
                    <span className="ul-btn__icon">
                      <i className="i-Close-Window text-18"></i>
                    </span>
                    <span className="ul-btn__text ml-2">Bulk Delete</span>
                  </Button>
                </div>

                {/* RIGHT SIDE ICONS */}
                <div className="d-flex align-items-center border-0">
                  <Button
                    className="btn-icon d-flex align-items-center justify-content-center me-1"
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: "#f44336",
                      border: "none",
                      borderRadius: "0",
                    }}
                  >
                    <i className="i-File-Horizontal-Text text-18 text-white"></i>
                  </Button>

                  <Button
                    className="btn-icon d-flex align-items-center justify-content-center me-1"
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: "#ffc107",
                      border: "none",
                      borderRadius: "0",
                    }}
                  >
                    <i className="i-File-CSV text-18 text-white"></i>
                  </Button>

                  <Button
                    className="btn-icon d-flex align-items-center justify-content-center me-1"
                    style={{
                      width: 36,
                      height: 36,
                      backgroundColor: "#007bff",
                      border: "none",
                      borderRadius: "0",
                    }}
                  >
                    <i className="i-Billing text-18 text-white"></i>
                  </Button>

                  <Dropdown align="end">
                    <Dropdown.Toggle
                      className="btn-icon d-flex align-items-center justify-content-center"
                      style={{
                        width: 36,
                        height: 36,
                        backgroundColor: "#663399",
                        border: "none",
                        borderRadius: 0,
                      }}
                    >
                      <i className="i-Eye text-18 text-white"></i>
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ backgroundColor: "#7d5bbe" }}>
                      <Dropdown.Item className="text-black">
                        Product Name
                      </Dropdown.Item>
                      <Dropdown.Item className="text-black">
                        Category
                      </Dropdown.Item>
                      <Dropdown.Item className="text-black">
                        Unit
                      </Dropdown.Item>
                      <Dropdown.Item className="text-black">
                        Unit Price
                      </Dropdown.Item>
                      <Dropdown.Item className="text-black">
                        Supplier
                      </Dropdown.Item>
                      <Dropdown.Item className="text-black">
                        Status
                      </Dropdown.Item>
                      <Dropdown.Item className="text-black">
                        Action
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {/* TABLE CARD */}
      <Card body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Card.Title>Product Details</Card.Title>

          {/* SEARCH RIGHT */}
          <div style={{ display: "flex", alignItems: "center" }}>
            {/* SEARCH INPUT */}
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "260px",
                height: "38px",
                border: "1px solid #d1d1d1",
                borderRight: "none",
                paddingLeft: "10px",
                outline: "none",
              }}
            />

            {/* SEARCH ICON */}
            <div
              style={{
                width: "40px",
                height: "38px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #d1d1d1",
                borderLeft: "none",
                background: "#f5f5f5",
              }}
            >
              <i className="i-Magnifi-Glass"></i>
            </div>

            {/* GRID ICON */}
            <div
              style={{
                width: "40px",
                height: "38px",
                backgroundColor: "#6f42c1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="i-Windows-2 text-18 text-white"></i>
            </div>

            {/* DOWNLOAD ICON */}
            <div
              onClick={handleDownloadPDF}
              style={{
                width: "40px",
                height: "38px",
                backgroundColor: "#6f42c1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderTopRightRadius: "6px",
                cursor: "pointer",
              }}
            >
              <i className="i-Download text-white"></i>
            </div>
          </div>
        </div>

        <Table bordered hover style={{ marginTop: "0" }}>
          <thead style={{ backgroundColor: "#cfc7d9" }}>
            <tr>
              {showCheckbox && (
                <th style={{ backgroundColor: "#cfc7d9" }}>
                  <input
                    type="checkbox"
                    style={{ transform: "scale(1.4)", cursor: "pointer" }}
                    checked={
                      paginatedProducts.length > 0 &&
                      paginatedProducts.every((item) =>
                        selectedRows.includes(item.id),
                      )
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRows(paginatedProducts.map((item) => item.id));
                      } else {
                        setSelectedRows([]);
                      }
                    }}
                  />
                </th>
              )}

              <th style={{ backgroundColor: "#cfc7d9" }}>No</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Product Name</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Category</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Unit</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Unit Price</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Supplier</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Status</th>
              <th style={{ backgroundColor: "#cfc7d9" }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {paginatedProducts.map((item, index) => (
              <tr key={item.id}>
                {showCheckbox && (
                  <td>
                    <input
                      type="checkbox"
                      style={{ transform: "scale(1.4)", cursor: "pointer" }}
                      checked={selectedRows.includes(item.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows((prev) => [...prev, item.id]);
                        } else {
                          setSelectedRows((prev) =>
                            prev.filter((id) => id !== item.id),
                          );
                        }
                      }}
                    />
                  </td>
                )}

                <td>{startIndex + index + 1}</td>
                <td>{item.product_name}</td>
                <td>{item.category_name || 'N/A'}</td>
                <td>{item.unit}</td>
                <td>₹{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                <td>{item.supplier_name || 'N/A'}</td>
                <td>
                  <span
                    className={`badge ${
                      item.status === "active"
                        ? "bg-success"
                        : "bg-secondary"
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleStatusToggle(item)}
                  >
                    {item.status}
                  </span>
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#663399",
                        border: "none",
                      }}
                      onClick={() => {
                        setEditData(item);
                        setCategoryId(item.category_id);
                        setProductName(item.product_name);
                        setProductDescription(item.product_description);
                        setStatus(item.status);
                        setUnitPrice(item.unit_price);
                        setSupplierId(item.supplier_id);
                        setUnit(item.unit);
                        setShowModal(true);
                      }}
                    >
                      <i className="i-Edit text-white"></i>
                    </Button>

                    <Button
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#c4302b",
                        border: "none",
                      }}
                      onClick={() => handleDeleteProduct(item.id)}
                    >
                      <i className="i-Close-Window text-white"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="d-flex justify-content-end align-items-center mt-3">
          <span className="me-2">Rows per page</span>

          <Form.Select
            value={showAll ? "all" : rowsPerPage}
            onChange={(e) => {
              if (e.target.value === "all") {
                setShowAll(true);
                setRowsPerPage(filteredProducts.length);
              } else {
                setShowAll(false);
                setRowsPerPage(Number(e.target.value));
              }
              setCurrentPage(1);
            }}
            style={{ width: "90px", marginRight: "15px" }}
          >
            <option value={5}>5</option>
            <option value={20}>20</option>
            <option value="all">All</option>
          </Form.Select>
          <span className="me-3">
            {startIndex + 1}-{Math.min(startIndex + rowsPerPage, totalRows)} of{" "}
            {totalRows}
          </span>

          {/* FIRST */}
          <Button
            style={{
              backgroundColor: "#7d5bbe",
              border: "none",
              marginRight: "5px",
            }}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            ⏮
          </Button>

          {/* PREVIOUS */}
          <Button
            style={{
              backgroundColor: "#7d5bbe",
              border: "none",
              marginRight: "5px",
            }}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            ◀
          </Button>

          {/* NEXT */}
          <Button
            style={{
              backgroundColor: "#7d5bbe",
              border: "none",
              marginRight: "5px",
            }}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            ▶
          </Button>

          {/* LAST */}
          <Button
            style={{ backgroundColor: "#7d5bbe", border: "none" }}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            ⏭
          </Button>
        </div>
      </Card>

      {/* MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editData ? "Edit Product" : "Add Product"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label>
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={category_id}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Label>
                  Product Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter product name"
                  value={product_name}
                  onChange={(e) => setProductName(e.target.value)}
                />
              </Col>

              <Col md={12} className="mb-3">
                <Form.Label>Product Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter product description"
                  value={product_description}
                  onChange={(e) => setProductDescription(e.target.value)}
                />
              </Col>

              <Col md={4} className="mb-3">
                <Form.Label>
                  Unit <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                >
                  <option value="">Select Unit</option>
                  {unitOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={4} className="mb-3">
                <Form.Label>
                  Unit Price <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  placeholder="Enter unit price"
                  value={unit_price}
                  onChange={(e) => setUnitPrice(e.target.value)}
                />
              </Col>

              <Col md={4} className="mb-3">
                <Form.Label>Supplier</Form.Label>
                <Form.Select
                  value={supplier_id}
                  onChange={(e) => setSupplierId(e.target.value)}
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.supplier_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-0 justify-content-end">
          <Button
            style={{
              backgroundColor: "#663399",
              color: "#fff",
              border: "none",
              padding: "8px 30px",
            }}
            onClick={editData ? handleUpdateProduct : handleAddProduct}
          >
            {editData ? "Update" : "Add"}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
